import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Package, Truck, Clock, CheckCircle2, XCircle, Archive, Search } from "lucide-react";

const STATUS_META = {
  submitted:    { label: "Submitted",    color: "bg-[var(--ft-light-gray)] text-[var(--ft-charcoal)] border border-[var(--ft-border)]", icon: Clock },
  under_review: { label: "Under Review", color: "bg-[#FFF8E9] text-[#8A6600] border border-[#F5D57A]", icon: Search },
  approved:     { label: "Approved",     color: "bg-[var(--ft-gold)] text-[var(--ft-black)]", icon: CheckCircle2 },
  rejected:     { label: "Rejected",     color: "bg-[#FFF0F0] text-[#9A1C1C] border border-[#F5C6C6]", icon: XCircle },
  shipped:      { label: "Shipped",      color: "bg-[#111111] text-white", icon: Truck },
  delivered:    { label: "Delivered",    color: "bg-[#E8F5EA] text-[#1A5C2C] border border-[#B8DDC0]", icon: Package },
  closed:       { label: "Closed",       color: "bg-[var(--ft-charcoal)] text-white/85", icon: Archive },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.submitted;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.625rem] font-bold tracking-[0.18em] uppercase ${meta.color}`}>
      <Icon size={11} />
      {meta.label}
    </span>
  );
};

const AdminSamples = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [stages, setStages] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("");

  const load = (status = filter) => {
    api.get(`/admin/sample-requests${status && status !== "all" ? `?status=${status}` : ""}`)
      .then((r) => {
        setRows(r.data.requests || []);
        setCounts(r.data.counts || {});
        setStages(r.data.stages || []);
      })
      .catch(() => toast.error("Failed to load sample requests"));
  };

  useEffect(() => { load(filter); }, [filter]);

  const openDetail = (r) => {
    setSelected(r);
    setNotes(r.admin_notes || "");
    setTracking(r.tracking_number || "");
    setCarrier(r.carrier || "");
  };

  const patch = async (payload) => {
    try {
      await api.patch(`/admin/sample-requests/${selected.id}`, payload);
      toast.success("Updated");
      load(filter);
      const fresh = await api.get(`/admin/sample-requests/${selected.id}`);
      setSelected(fresh.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const advance = (nextStatus) => patch({ status: nextStatus, admin_notes: notes || null });

  const saveDetails = () => patch({
    admin_notes: notes || null,
    tracking_number: tracking || null,
    carrier: carrier || null,
  });

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="ft-eyebrow mb-2">CRM Pipeline</div>
        <h1 className="text-3xl font-bold tracking-tight text-[var(--ft-black)]">Sample Request Center</h1>
        <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
          Qualification-driven sample fulfillment. Review each request, approve or reject based on
          fit, then track through shipment, delivery, and close.
        </p>
      </div>

      {/* Pipeline pills */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="samples-filter-bar">
        {[{ key: "all", label: "All" }, ...stages].map((s) => {
          const active = filter === s.key;
          const count = counts[s.key] ?? 0;
          return (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              data-testid={`samples-filter-${s.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                active
                  ? "bg-[var(--ft-black)] text-white"
                  : "bg-white border border-[var(--ft-border)] text-[var(--ft-charcoal)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]"
              }`}
            >
              {s.label}
              <span className={`text-[0.65rem] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white/15" : "bg-[var(--ft-light-gray)]"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="ft-card p-0 overflow-hidden">
        <table className="ft-table" data-testid="samples-table">
          <thead>
            <tr>
              <th>Requester</th>
              <th>Company</th>
              <th>Products</th>
              <th>Status</th>
              <th>Submitted</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[var(--ft-muted)] py-12">No sample requests in this stage yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`sample-row-${r.id}`}>
                <td>
                  <div className="font-semibold text-[var(--ft-black)]">{r.full_name}</div>
                  <div className="text-xs text-[var(--ft-muted)]">{r.email}</div>
                </td>
                <td className="text-sm">
                  <div>{r.company || <span className="italic text-[var(--ft-muted)]">—</span>}</div>
                  {r.role_title && <div className="text-xs text-[var(--ft-muted)]">{r.role_title}</div>}
                </td>
                <td className="text-xs text-[var(--ft-charcoal)]">
                  {(r.product_interest || []).length} item{(r.product_interest || []).length === 1 ? "" : "s"}
                </td>
                <td><StatusPill status={r.status} /></td>
                <td className="text-xs text-[var(--ft-muted)]">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                </td>
                <td>
                  <button onClick={() => openDetail(r)} className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]" data-testid={`sample-open-${r.id}`}>
                    Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="sample-detail">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="ft-eyebrow mb-1">Sample Request</div>
                <h2 className="text-2xl font-bold tracking-tight">{selected.full_name}</h2>
                <div className="text-sm text-[var(--ft-muted)] mt-1">{selected.email}{selected.phone ? ` · ${selected.phone}` : ""}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[var(--ft-muted)] hover:text-[var(--ft-black)]"><X size={22} /></button>
            </div>

            <div className="mb-6 flex items-center gap-3 flex-wrap">
              <StatusPill status={selected.status} />
              <div className="text-xs text-[var(--ft-muted)]">
                Submitted {selected.created_at ? new Date(selected.created_at).toLocaleString() : ""}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="ft-card-flat">
                <div className="ft-eyebrow mb-2">Company</div>
                <div className="text-sm text-[var(--ft-charcoal)]">
                  <div><span className="text-[var(--ft-muted)]">Brand:</span> {selected.company || "—"}</div>
                  <div><span className="text-[var(--ft-muted)]">Role:</span> {selected.role_title || "—"}</div>
                </div>
              </div>
              <div className="ft-card-flat">
                <div className="ft-eyebrow mb-2">Qualification</div>
                <div className="text-sm text-[var(--ft-charcoal)] space-y-1">
                  <div><span className="text-[var(--ft-muted)]">Volume:</span> {selected.estimated_volume || "—"}</div>
                  <div><span className="text-[var(--ft-muted)]">Launch:</span> {selected.target_launch || "—"}</div>
                </div>
              </div>
              <div className="ft-card-flat md:col-span-2">
                <div className="ft-eyebrow mb-2">Products Requested</div>
                <div className="flex flex-wrap gap-2">
                  {(selected.product_interest || []).map((slug) => (
                    <span key={slug} className="text-xs px-3 py-1 rounded-full bg-[var(--ft-light-gray)] font-medium">{slug}</span>
                  ))}
                </div>
              </div>
              <div className="ft-card-flat md:col-span-2">
                <div className="ft-eyebrow mb-2">Intended Use</div>
                <p className="text-sm text-[var(--ft-charcoal)] leading-relaxed">{selected.intended_use || <span className="italic text-[var(--ft-muted)]">Not provided</span>}</p>
              </div>
              <div className="ft-card-flat md:col-span-2">
                <div className="ft-eyebrow mb-2">Ship To</div>
                <div className="text-sm text-[var(--ft-charcoal)] leading-relaxed">
                  {[selected.ship_address_line1, selected.ship_address_line2].filter(Boolean).join(", ") || <span className="italic text-[var(--ft-muted)]">No address provided</span>}
                  <div className="text-xs text-[var(--ft-muted)] mt-1">
                    {[selected.ship_city, selected.ship_state, selected.ship_postal_code, selected.ship_country].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-[var(--ft-border)] pt-6 space-y-4">
              <div>
                <label className="ft-label">Admin Notes</label>
                <textarea rows={3} className="ft-input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sample-notes" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ft-label">Carrier</label>
                  <input className="ft-input" placeholder="UPS, FedEx, USPS…" value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="sample-carrier" />
                </div>
                <div>
                  <label className="ft-label">Tracking Number</label>
                  <input className="ft-input" value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="sample-tracking" />
                </div>
              </div>
              <button onClick={saveDetails} className="ft-btn-ghost" data-testid="sample-save-details">Save Details</button>
            </div>

            <div className="border-t border-[var(--ft-border)] pt-6 mt-6">
              <div className="ft-eyebrow mb-3">Advance Stage</div>
              <div className="flex flex-wrap gap-2">
                {stages.filter((s) => s.key !== selected.status).map((s) => (
                  <button
                    key={s.key}
                    onClick={() => advance(s.key)}
                    className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]"
                    data-testid={`sample-advance-${s.key}`}
                  >
                    → {s.label}
                  </button>
                ))}
              </div>
            </div>

            {selected.status_history && selected.status_history.length > 0 && (
              <div className="border-t border-[var(--ft-border)] pt-6 mt-6">
                <div className="ft-eyebrow mb-3">History</div>
                <ol className="space-y-2 text-xs">
                  {selected.status_history.map((h, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <StatusPill status={h.status} />
                      <div>
                        <div className="text-[var(--ft-charcoal)]">by {h.by}</div>
                        <div className="text-[var(--ft-muted)]">{h.at ? new Date(h.at).toLocaleString() : ""}</div>
                        {h.note && <div className="text-[var(--ft-muted)] italic mt-0.5">&quot;{h.note}&quot;</div>}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSamples;
